import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { baseURL } from '../config';
import { FaEdit, FaHistory, FaRupeeSign, FaPhone, FaUser, FaCloudUploadAlt, FaKey, FaCamera } from 'react-icons/fa';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import FinancialReport from '../components/reusable/financialDashboard';
import UserList from '../components/reusable/usersList';
import PageHeader from '../components/reusable/PageHeader';

function UserDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const passedUser = location.state;
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [formData, setFormData] = useState({});
  const [profileImageFile, setProfileImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [userId, setUserId] = useState(() => {
    const initialUserId = passedUser?.id || id;
    return initialUserId ? String(initialUserId) : "";
  });
  const [username, setUserName] = useState();
  const [isResetModalOpen, setIsResetModalOpen] = useState(false);
  const [resetPasswordData, setResetPasswordData] = useState({
    newPassword: '',
    confirmPassword: ''
  });
  
  const fileInputRef = useRef(null);
  const userRequestIdRef = useRef(0);

  useEffect(() => {
    if (passedUser) {
      setUserId(String(passedUser.id || id));
      setUserName(passedUser.username);
      setFormData(passedUser);
      setImagePreview(passedUser.profile_img);
      setLoading(false);
      setUser(passedUser);
    } else if (id) {
      setUserId(String(id));
    }
  }, [id, passedUser]);

  useEffect(() => {
    if (!userId) return;
    const requestId = ++userRequestIdRef.current;
    setLoading(true);
    axios.post(`${baseURL}/singleUser`, { userId })
      .then(({ data }) => {
        if (requestId !== userRequestIdRef.current) return;
        const user = data[0] || {};
        setUser(user);
        setFormData(user);
        setImagePreview(user.profile_img);
        setUserName(user.username);
      })
      .catch((err) => {
        if (requestId === userRequestIdRef.current) {
          console.error("Failed to fetch user:", err);
        }
      })
      .finally(() => {
        if (requestId === userRequestIdRef.current) {
          setLoading(false);
        }
      });

    return () => {
      userRequestIdRef.current += 1;
    };
  }, [userId]);

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setProfileImageFile(file);
    const reader = new FileReader();
    reader.onloadend = () => setImagePreview(reader.result);
    reader.readAsDataURL(file);
  };

  const triggerFileInput = () => fileInputRef.current.click();
  const validatePhone = (phone) => /^\d{10}$/.test(phone);
  const validatePassword = (password) => /^(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/.test(password);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) return toast.error("Name is required");
    if (formData.mobile_number && !validatePhone(formData.mobile_number)) return toast.error("Phone number must be 10 digits");

    try {
      const form = new FormData();
      form.append("name", formData.name);
      form.append("mobileNumber", formData.mobile_number);
      if (profileImageFile) form.append("image", profileImageFile);

      const response = await axios.put(`${baseURL}/editUser/${id}`, form, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
          "Content-Type": "multipart/form-data",
        },
      });

      setUser(response.data.user);
      setEditMode(false);
      setProfileImageFile(null);
      toast.success("Profile updated successfully!");
    } catch (error) {
      console.error("Error updating user:", error);
      toast.error("Failed to update profile.");
    }
  };

  const cancelEdit = () => {
    setEditMode(false);
    setProfileImageFile(null);
    setImagePreview(user.profile_img);
  };

  const openResetModal = () => setIsResetModalOpen(true);
  const closeResetModal = () => {
    setIsResetModalOpen(false);
    setResetPasswordData({ newPassword: '', confirmPassword: '' });
  };

  const handleResetPasswordChange = (e) => {
    const { name, value } = e.target;
    setResetPasswordData(prev => ({ ...prev, [name]: value }));
  };

  const handleResetSubmit = async () => {
    if (!resetPasswordData.newPassword || !resetPasswordData.confirmPassword) return toast.error("Both fields required");
    if (resetPasswordData.newPassword !== resetPasswordData.confirmPassword) return toast.error("Passwords do not match");
    if (!validatePassword(resetPasswordData.newPassword)) return toast.error("Password must be 8+ chars with uppercase, number & special char");

    try {
      const response = await axios.post(`${baseURL}/user/resetpassword`, {
        userId: user.id,
        username: user.username,
        newPassword: resetPasswordData.newPassword
      }, { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } });
      
      if (response.data.success) {
        toast.success("Password reset successfully!");
        closeResetModal();
      } else {
        toast.error("Failed to reset password.");
      }
    } catch (error) {
      console.error("Error resetting password:", error);
      toast.error("Error occurred while resetting");
    }
  };

  if (!user) return <div className="w-full h-screen flex items-center justify-center bg-paper text-primary font-bold">Loading...</div>;

  return (
    <div className="min-h-screen w-full bg-paper pb-20 font-sans text-primary">
      <main className="max-w-lg mx-auto px-3 pt-4 space-y-4">
        <PageHeader title="Customer Profile" />
        
        {/* 1. ULTRA COMPACT USER SELECTOR */}
        <div className="bg-white border border-line rounded-2xl shadow-sm p-3">
          <UserList onSelectUser={setUserId} selectedUser={username} required={false} />
        </div>

        {/* 2. QUICK NAV BUTTONS */}
        <div className="grid grid-cols-2 gap-2">
          <button onClick={() => navigate(`/view-entries`, { state: user })} className="flex items-center justify-center gap-2 bg-white border border-line text-primary py-3 rounded-xl font-bold text-[12.5px] shadow-sm active:scale-95 transition-all">
            <FaHistory className="text-primary" /> Milk Entries
          </button>
          <button onClick={() => navigate(`/balance`, { state: user })} className="flex items-center justify-center gap-2 bg-white border border-line text-primary py-3 rounded-xl font-bold text-[12.5px] shadow-sm active:scale-95 transition-all">
            <FaRupeeSign className="text-primary" /> View Balance
          </button>
          <button onClick={() => setEditMode(true)} className="flex items-center justify-center gap-2 bg-white border border-line text-[#6B7280] hover:text-primary hover:border-primary/50 py-3 rounded-xl font-bold text-[12.5px] shadow-sm active:scale-95 transition-all">
            <FaEdit /> Edit Profile
          </button>
          <button onClick={openResetModal} className="flex items-center justify-center gap-2 bg-white border border-line text-[#6B7280] hover:text-primary hover:border-primary/50 py-3 rounded-xl font-bold text-[12.5px] shadow-sm active:scale-95 transition-all">
            <FaKey /> Reset Password
          </button>
        </div>

        {/* 3. FINANCIAL REPORT (Now manages its own dates!) */}
        <div className="pt-2">
          <FinancialReport userId={userId} userName={user.name} userMobile={user.username} />
        </div>

      </main>

      {/* --- EDIT PROFILE MODAL --- */}
      {editMode && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-[100] p-4">
          <div className="bg-white border border-line rounded-2xl shadow-2xl w-full max-w-sm overflow-hidden animate-bounce-up">
            <div className="bg-paper/80 px-5 py-4 border-b border-line flex justify-between items-center">
              <h2 className="text-[15px] font-bold text-primary tracking-wide">Edit Profile</h2>
              <button onClick={cancelEdit} className="text-[#6B7280] hover:text-primary text-xl">&times;</button>
            </div>
            <form onSubmit={handleSubmit} className="p-5 space-y-4">
              <div className="flex items-center gap-4 p-3 bg-paper/30 border border-line rounded-xl">
                <div className="relative">
                  {imagePreview ? (
                    <img src={imagePreview} alt="Preview" className="w-14 h-14 rounded-full border border-line object-cover" />
                  ) : (
                    <div className="w-14 h-14 rounded-full bg-paper flex items-center justify-center border border-line">
                      <FaUser className="text-[#6B7280] text-xl" />
                    </div>
                  )}
                  <button type="button" onClick={triggerFileInput} className="absolute -bottom-1 -right-1 bg-primary text-white rounded-full p-1.5 shadow-md active:scale-90">
                    <FaCamera className="text-[10px]" />
                  </button>
                  <input type="file" ref={fileInputRef} onChange={handleImageUpload} accept="image/*" className="hidden" />
                </div>
                <div className="text-[11px] text-[#6B7280] font-medium leading-tight flex-1">
                  <p>Tap camera icon to update.</p>
                  {profileImageFile && <p className="text-primary font-bold mt-1 truncate max-w-[150px]">{profileImageFile.name}</p>}
                </div>
              </div>
              <div>
                <label className="block text-[10px] font-bold uppercase tracking-widest text-primary mb-1.5 ml-0.5">Full Name</label>
                <input type="text" name="name" value={formData.name || ""} onChange={handleInputChange} className="w-full px-4 py-3 bg-white border border-line rounded-xl text-[13px] font-bold text-primary focus:outline-none focus:border-primary shadow-sm" required />
              </div>
              <div>
                <label className="block text-[10px] font-bold uppercase tracking-widest text-primary mb-1.5 ml-0.5">Mobile Number</label>
                <input type="tel" name="mobile_number" value={formData.mobile_number || ""} onChange={handleInputChange} className="w-full px-4 py-3 bg-white border border-line rounded-xl text-[13px] font-bold text-primary focus:outline-none focus:border-primary shadow-sm" pattern="[0-9]{10}" required />
              </div>
              <div className="pt-3 flex justify-end gap-3">
                <button type="button" onClick={cancelEdit} className="px-4 py-2.5 text-[12.5px] font-bold text-[#6B7280] bg-white border border-line rounded-xl shadow-sm">Cancel</button>
                <button type="submit" className="px-4 py-2.5 text-[12.5px] font-bold text-white bg-primary rounded-xl active:scale-95 shadow-sm">Save Changes</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* --- RESET PASSWORD MODAL --- */}
      {isResetModalOpen && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-[100] p-4">
          <div className="bg-white border border-line rounded-2xl shadow-2xl w-full max-w-sm overflow-hidden animate-bounce-up">
            <div className="bg-paper/80 px-5 py-4 border-b border-line">
              <h2 className="text-[15px] font-bold text-primary tracking-wide">Reset Password</h2>
              <p className="text-[11px] text-[#6B7280] font-medium mt-1">For account: <span className="font-bold text-primary">{user.name}</span></p>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <label className="block text-[10px] font-bold uppercase tracking-widest text-primary mb-1.5 ml-0.5">New Password</label>
                <input type="password" name="newPassword" value={resetPasswordData.newPassword} onChange={handleResetPasswordChange} className="w-full px-4 py-3 bg-white border border-line rounded-xl text-[13px] font-medium focus:outline-none focus:border-primary shadow-sm" placeholder="Enter new password" />
              </div>
              <div>
                <label className="block text-[10px] font-bold uppercase tracking-widest text-primary mb-1.5 ml-0.5">Confirm Password</label>
                <input type="password" name="confirmPassword" value={resetPasswordData.confirmPassword} onChange={handleResetPasswordChange} className="w-full px-4 py-3 bg-white border border-line rounded-xl text-[13px] font-medium focus:outline-none focus:border-primary shadow-sm" placeholder="Confirm new password" />
              </div>
            </div>
            <div className="bg-paper/50 px-5 py-4 border-t border-line flex justify-end gap-3">
              <button onClick={closeResetModal} className="px-4 py-2.5 text-[12.5px] font-bold text-[#6B7280] bg-white border border-line rounded-xl shadow-sm">Cancel</button>
              <button onClick={handleResetSubmit} className="px-4 py-2.5 text-[12.5px] font-bold text-white bg-debit rounded-xl active:scale-95 shadow-sm">Reset Password</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default UserDetail;
